import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Close, DeleteOutline, Edit, StickyNote2 } from '@mui/icons-material';
import { colors } from '../theme/theme';
import { useAuth } from '../contexts/AuthContext';
import { isStudioScopedRole } from '../utils/roles';
import {
  currentNoteStudioId,
  MAX_NOTE_LENGTH,
  noteEditedByLabel,
  noteStudioId,
  noteStudioLabel,
  UserNote,
  userNotesApi,
} from '../services/userNotesApi';

/** A save (`saved`) or a delete (`removedId`) on one of the member's notes. */
type NoteChange = (userId: string, saved: UserNote | null, removedId?: string) => void;

interface UserNoteDialogProps {
  open: boolean;
  user: { _id: string; name?: string } | null;
  /** Every note on the member the viewer may read — one per location. */
  notes: UserNote[];
  /**
   * The location the text box writes to: the booking's on the bookings page,
   * the switcher's elsewhere. Null = none (super admin, or "All locations"
   * with no booking to pin it to) — notes in the list are still editable.
   */
  targetStudioId: string | null;
  /** Shown when there's no target — e.g. "pick a location" under All. */
  hint?: string;
  onClose: () => void;
  onChange?: NoteChange;
}

const errorMessage = (err: any) => err?.message || 'Something went wrong';

const noteTextSx = { whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.6 } as const;

/** "Purple Pilates · Indiranagar — last edited by Rahul (Manager)" over the text. */
const NoteItem: React.FC<{ note: UserNote; onEdit?: () => void }> = ({ note, onEdit }) => {
  const editedBy = noteEditedByLabel(note);
  return (
    <Box sx={{ '&:not(:last-of-type)': { mb: 2 } }}>
      <Box display="flex" alignItems="center" gap={0.5}>
        <Typography variant="caption" fontWeight={700} sx={{ color: colors.primaryDark, flex: 1, minWidth: 0 }}>
          {noteStudioLabel(note)}
          {editedBy && (
            <Typography component="span" variant="caption" color={colors.textSecondary}> — {editedBy}</Typography>
          )}
        </Typography>
        {onEdit && (
          <Tooltip title="Edit note">
            <IconButton size="small" onClick={onEdit} sx={{ color: colors.primaryDark }}>
              <Edit sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Typography variant="body2" sx={noteTextSx}>{note.text}</Typography>
    </Box>
  );
};

const NoteList: React.FC<{ notes: UserNote[]; onEdit?: (note: UserNote) => void }> = ({ notes, onEdit }) => (
  <>
    {notes.map((note) => (
      <NoteItem key={note._id} note={note} onEdit={onEdit && note.canEdit ? () => onEdit(note) : undefined} />
    ))}
  </>
);

/**
 * A member's notes — one per location, shared by its staff. The text box
 * edits one location's note: `targetStudioId`'s by default, or another
 * location's picked from the list. Each note is saved at its own location.
 */
const UserNoteDialog: React.FC<UserNoteDialogProps> = ({ open, user, notes, targetStudioId, hint, onClose, onChange }) => {
  // The note picked from the list; null = the target location's (new or existing).
  const [picked, setPicked] = useState<UserNote | null>(null);
  const atTarget = targetStudioId ? notes.find((n) => noteStudioId(n) === targetStudioId) || null : null;
  const current = picked || atTarget;
  const editingStudio = picked ? noteStudioId(picked) : targetStudioId;
  const hasEditor = !!editingStudio || !!picked;
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (open) {
      setPicked(null);
      setText(atTarget?.text || '');
      setError('');
      setConfirmDelete(false);
    }
    // Reset only when the dialog opens for a member, not on every save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user?._id, targetStudioId]);

  if (!user) return null;
  const trimmed = text.trim();
  const unchanged = trimmed === (current?.text || '');
  const listed = notes.filter((n) => n !== current);
  const editingLabel = current ? noteStudioLabel(current) : 'your studio';

  const pick = (note: UserNote) => {
    setPicked(note);
    setText(note.text);
    setConfirmDelete(false);
    setError('');
  };

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const save = () =>
    run(async () => {
      const saved = await userNotesApi.save(user._id, trimmed, editingStudio as string, current);
      onChange?.(user._id, saved);
    });

  const remove = () =>
    run(async () => {
      if (!current) return;
      await userNotesApi.remove(current);
      onChange?.(user._id, null, current._id);
    });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" noWrap>{user.name || 'Member'}</Typography>
          <Typography variant="caption" color={colors.textSecondary}>
            {hasEditor ? `Member note · ${editingLabel}` : 'Member notes'}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!hasEditor && hint && <Alert severity="info" sx={{ mb: 2 }}>{hint}</Alert>}

        {hasEditor && (
          <TextField
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Write a note about this member"
            fullWidth
            multiline
            minRows={10}
            maxRows={20}
            autoFocus
            inputProps={{ maxLength: MAX_NOTE_LENGTH }}
            helperText={current && noteEditedByLabel(current) ? `${noteEditedByLabel(current)} · ${text.length}/${MAX_NOTE_LENGTH}` : `${text.length}/${MAX_NOTE_LENGTH}`}
            FormHelperTextProps={{ sx: { textAlign: 'right', mx: 0 } }}
          />
        )}

        {listed.length > 0 && (
          <Box sx={hasEditor ? { mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' } : undefined}>
            <NoteList notes={listed} onEdit={pick} />
          </Box>
        )}

        {!hasEditor && !hint && listed.length === 0 && (
          <Typography variant="body2" color={colors.textSecondary}>No notes</Typography>
        )}
      </DialogContent>

      {hasEditor && (
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          {current && (
            confirmDelete ? (
              <>
                <Typography variant="body2" sx={{ mr: 'auto' }}>Delete this note?</Typography>
                <Button onClick={() => setConfirmDelete(false)} disabled={busy}>Cancel</Button>
                <Button color="error" onClick={remove} disabled={busy}>Delete</Button>
              </>
            ) : (
              <Button color="error" onClick={() => setConfirmDelete(true)} disabled={busy} sx={{ mr: 'auto' }}>
                Delete
              </Button>
            )
          )}
          {!confirmDelete && (
            <Button
              variant="contained"
              onClick={save}
              disabled={busy || !trimmed || unchanged}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              Save
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default UserNoteDialog;

/** Who sees notes: studio staff (their locations), super admins (all). */
export type NotesMode = 'studio' | 'admin' | null;

export const notesModeFor = (role?: string | null): NotesMode =>
  isStudioScopedRole(role) ? 'studio' : role === 'super_admin' || role === 'admin' ? 'admin' : null;

/**
 * Notes for the members on a page, keyed by user id — every note the viewer
 * may read, one per location. Fetches only ids it hasn't seen, so paging /
 * appending rows asks for the new ones alone.
 */
export function useUserNotes(userIds: (string | undefined | null)[], mode: NotesMode) {
  const [notesByUser, setNotesByUser] = useState<Record<string, UserNote[]>>({});
  const fetchedRef = useRef<Set<string>>(new Set());
  const key = Array.from(new Set(userIds.filter(Boolean) as string[])).join(',');

  useEffect(() => {
    if (!mode || !key) return;
    const ids = key.split(',').filter((id) => !fetchedRef.current.has(id));
    if (!ids.length) return;
    ids.forEach((id) => fetchedRef.current.add(id));
    const load = mode === 'studio' ? userNotesApi.forUsers : userNotesApi.adminForUsers;
    for (let i = 0; i < ids.length; i += 500) {
      const chunk = ids.slice(i, i + 500);
      load(chunk)
        .then((notes) => {
          const next: Record<string, UserNote[]> = {};
          chunk.forEach((id) => { next[id] = []; });
          (notes || []).forEach((n) => { next[String(n.user)]?.push(n); });
          setNotesByUser((prev) => ({ ...prev, ...next }));
        })
        .catch(() => chunk.forEach((id) => fetchedRef.current.delete(id)));
    }
  }, [key, mode]);

  const updateNote: NoteChange = (userId, saved, removedId) =>
    setNotesByUser((prev) => {
      const drop = saved?._id ?? removedId;
      const rest = (prev[userId] || []).filter((n) => n._id !== drop);
      return { ...prev, [userId]: saved ? [saved, ...rest] : rest };
    });

  return { notesByUser, updateNote };
}

/** Label for the row's menu, or null when there's nothing to show. */
export const noteMenuLabel = (mode: NotesMode, hasNote: boolean): string | null =>
  mode === 'studio' ? 'Member note' : mode === 'admin' && hasNote ? 'Member notes' : null;

/**
 * The member's notes shown in place on the user and booking detail pages —
 * one per location, each with who last edited it. Studio staff get an edit /
 * add button for `studioId` (the booking's location) or the switcher's.
 * Super admin: every studio's, hidden when there are none.
 */
export const UserNotePanel: React.FC<{
  userId: string;
  userName?: string;
  /**
   * The booking's location on a booking page; omitted = the switcher's (the
   * member's own page, which instructors don't get notes on).
   */
  studioId?: string | null;
  /** Inside an existing card (no border of its own). */
  inline?: boolean;
}> = ({ userId, userName, studioId, inline }) => {
  const { admin } = useAuth();
  // Instructors only get notes on a booking (studioId given), never on the
  // member's own page.
  const mode = admin?.role === 'instructor' && !studioId ? null : notesModeFor(admin?.role);
  const { notesByUser, updateNote } = useUserNotes([userId], mode);
  const [open, setOpen] = useState(false);

  const notes = notesByUser[userId];
  if (!mode || !notes) return null;
  const target = mode === 'studio' ? studioId || currentNoteStudioId() : null;
  const canOpen = !!target || notes.some((n) => n.canEdit);
  if (!canOpen && notes.length === 0) return null;
  const atTarget = target ? notes.find((n) => noteStudioId(n) === target) : undefined;

  const body = (
    <>
      <Box display="flex" alignItems="center" gap={1} mb={notes.length ? 0.5 : 0}>
        <StickyNote2 sx={{ fontSize: 18, color: colors.primary }} />
        <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>
          {notes.length > 1 ? 'Member notes' : 'Member note'}
        </Typography>
        {canOpen && (
          <Tooltip title={atTarget || !target ? 'Edit note' : 'Add note'}>
            <IconButton size="small" onClick={() => setOpen(true)} sx={{ color: colors.primaryDark }}>
              {atTarget || !target ? <Edit sx={{ fontSize: 18 }} /> : <Add sx={{ fontSize: 20 }} />}
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {/* One per location, each with who last edited it — this location's first. */}
      <NoteList notes={atTarget ? [atTarget, ...notes.filter((n) => n !== atTarget)] : notes} />
      {canOpen && (
        <UserNoteDialog
          open={open}
          user={{ _id: userId, name: userName }}
          notes={notes}
          targetStudioId={target}
          onClose={() => setOpen(false)}
          onChange={updateNote}
        />
      )}
    </>
  );

  return inline ? (
    <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>{body}</Box>
  ) : (
    <Box sx={{ p: 2, mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>{body}</Box>
  );
};

/**
 * A small popup on a booking row: the member's note at that booking's
 * location, read and edited in place — no dialog, no navigation. Studio
 * staff create / edit / delete it; a super admin edits or deletes an
 * existing one.
 */
export const UserNotePopover: React.FC<{
  anchorEl: HTMLElement | null;
  user: { _id: string; name?: string } | null;
  /** The booking's location. */
  studioId: string | null;
  /** Shown when the location has no note yet (e.g. the booking's studio name). */
  studioLabel?: string;
  notes: UserNote[];
  mode: NotesMode;
  onClose: () => void;
  onChange?: NoteChange;
}> = ({ anchorEl, user, studioId, studioLabel, notes, mode, onClose, onChange }) => {
  const note = studioId ? notes.find((n) => noteStudioId(n) === studioId) || null : null;
  const canWrite = mode === 'studio' ? !!studioId : mode === 'admin' && !!note;
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (anchorEl) {
      setText(note?.text || '');
      // Nothing to read yet → straight to writing.
      setEditing(!note && canWrite);
      setError('');
      setConfirmDelete(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorEl, user?._id, studioId]);

  if (!user) return null;
  const trimmed = text.trim();
  const editedBy = note ? noteEditedByLabel(note) : '';

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const save = () =>
    run(async () => {
      const saved = await userNotesApi.save(user._id, trimmed, studioId as string, note);
      onChange?.(user._id, saved);
    });

  const remove = () =>
    run(async () => {
      if (!note) return;
      await userNotesApi.remove(note);
      onChange?.(user._id, null, note._id);
    });

  const cancel = () => {
    if (!note) return onClose();
    setEditing(false);
    setText(note.text);
    setError('');
  };

  return (
    <Popover
      open={!!anchorEl}
      anchorEl={anchorEl}
      onClose={onClose}
      onClick={(e) => e.stopPropagation()}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{
        elevation: 0,
        sx: {
          width: 320,
          mt: 0.5,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
        },
      }}
    >
      {/* Location — the note belongs to this booking's studio. */}
      <Box display="flex" alignItems="center" sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
        <Typography
          variant="caption"
          sx={{ flex: 1, minWidth: 0, color: colors.textSecondary, fontWeight: 600, letterSpacing: 0.2 }}
          noWrap
        >
          {note ? noteStudioLabel(note) : studioLabel || 'Member note'}
        </Typography>
        {!editing && canWrite && note && (
          <>
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => setEditing(true)} sx={{ color: colors.textSecondary }}>
                <Edit sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={confirmDelete ? 'Click again to delete' : 'Delete'}>
              <IconButton
                size="small"
                disabled={busy}
                onClick={() => (confirmDelete ? remove() : setConfirmDelete(true))}
                sx={{ color: confirmDelete ? colors.error : colors.textSecondary }}
              >
                <DeleteOutline sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>

      <Box sx={{ px: 2, pb: 1.5 }}>
        {error && (
          <Typography variant="caption" color={colors.error} display="block" sx={{ mb: 0.5 }}>
            {error}
          </Typography>
        )}

        {editing ? (
          <>
            <TextField
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Write a note about this member"
              fullWidth
              multiline
              minRows={4}
              maxRows={10}
              autoFocus
              size="small"
              inputProps={{ maxLength: MAX_NOTE_LENGTH }}
              sx={{ mt: 0.5, '& .MuiInputBase-root': { fontSize: 14 } }}
            />
            <Box display="flex" justifyContent="flex-end" gap={1} mt={1}>
              <Button size="small" onClick={cancel} disabled={busy} sx={{ color: colors.textSecondary }}>
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                disableElevation
                onClick={save}
                disabled={busy || !trimmed || trimmed === (note?.text || '')}
              >
                {busy ? <CircularProgress size={14} color="inherit" /> : 'Save'}
              </Button>
            </Box>
          </>
        ) : note ? (
          <>
            <Typography variant="body2" sx={{ ...noteTextSx, maxHeight: 220, overflowY: 'auto' }}>
              {note.text}
            </Typography>
            {editedBy && (
              <Typography variant="caption" color={colors.textSecondary} display="block" sx={{ mt: 1 }}>
                {editedBy.charAt(0).toUpperCase() + editedBy.slice(1)}
              </Typography>
            )}
          </>
        ) : (
          <Typography variant="body2" color={colors.textSecondary}>No note</Typography>
        )}
      </Box>
    </Popover>
  );
};
